import { Info, UserIcon, UserPlusIcon } from "@phosphor-icons/react/dist/ssr";
import { countryCodes } from "apps/visits/packages/shared/static/country-codes";
import { Form } from "apps/visits/packages/ui/src/components";
import type React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import { Input } from "@/core/shared/v2/ui/input";
import { Label } from "@/core/shared/v2/ui/label";
import { Select } from "@/core/shared/v2/ui/select";
import type { PhoneCheckoutStep, UserData } from "../../../hooks/use-phone-checkout-flow";
import type { ConfigName } from "../../../types/config-name";
import type { FormValues } from "../../user-data-form/validation-schema";
import { ConsentsSection } from "./consents-section";
import { PeselSection } from "./pesel-section";

type CustomerSectionProps = {
  variant: "friends-and-family" | "standard";
  configName: ConfigName;
  description?: string;
  phoneAuthContent?: React.ReactNode;
  phoneCheckoutStep?: PhoneCheckoutStep;
  showGuestOption: boolean;
  isGuestCheckout: boolean;
  onGuestCheckoutToggle: (guest: boolean) => void;
  readonlyCustomerFields: boolean;
  prefilledUserData?: UserData | null;
  showPasswordFields: boolean;
  values: FormValues;
  // Embedded pesel section (standard variant) + consents
  coreRequired: boolean;
  noPesel?: boolean;
  orderType: FormValues["orderType"];
  showMarketingConsent: boolean;
  isSelectAllChecked: boolean;
  consentFieldsForSelectAll: readonly (keyof FormValues)[];
  privacyPolicyHref: string;
  termsHref: string;
};

export const CustomerSection = ({
  variant,
  configName,
  description,
  phoneAuthContent,
  phoneCheckoutStep,
  showGuestOption,
  isGuestCheckout,
  onGuestCheckoutToggle,
  readonlyCustomerFields,
  prefilledUserData,
  showPasswordFields,
  values,
  coreRequired,
  noPesel,
  orderType,
  showMarketingConsent,
  isSelectAllChecked,
  consentFieldsForSelectAll,
  privacyPolicyHref,
  termsHref,
}: CustomerSectionProps) => {
  const { t } = useTranslation();
  const { register, errors, control } = useFormContext<FormValues>();

  // F&F numbers this section "2." because the patient card is "1."; standard
  // variants only have the orderer section so no numbering. F&F continues to
  // pull copy from cmsTranslations (per-config CMS-driven text); the standard
  // variants take the description from the SharedCheckout prop — same content
  // that used to live under the page heading.
  const customerSectionDescription =
    variant === "friends-and-family" ? t(`cmsTranslations.${configName}.user_data_form.description`) : description;

  return (
    <>
      <div>
        <h3 className="font-serif text-2xl">
          {variant === "friends-and-family" ? "2. " : ""}
          {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.CUSTOMER_DATA")}
        </h3>
        {customerSectionDescription && (
          <p className="mt-1 max-w-[720px] text-pretty text-muted-foreground text-sm">{customerSectionDescription}</p>
        )}
      </div>

      {phoneAuthContent}

      {(!phoneCheckoutStep ||
        phoneCheckoutStep === "NewUser" ||
        phoneCheckoutStep === "NewUserOTPOverlay" ||
        phoneCheckoutStep === "DataLoaded") && (
        <div
          className={
            phoneCheckoutStep
              ? "fade-in slide-in-from-top-4 flex animate-in flex-col gap-5 fill-mode-both duration-500"
              : "flex flex-col gap-5"
          }
        >
          <input type="hidden" ref={register} name="isGuestCheckout" />

          {showGuestOption && (
            <div className="fade-in slide-in-from-top-2 animate-in fill-mode-both duration-300">
              <div className="flex gap-1.5 rounded-lg bg-gray-50 p-1">
                <button
                  type="button"
                  className={cn(
                    "flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-transparent px-4 py-2 text-xs transition-colors",
                    !isGuestCheckout
                      ? "border-dodger-blue-700 bg-white text-foreground"
                      : "text-muted-foreground hover:bg-gray-100",
                  )}
                  onClick={() => onGuestCheckoutToggle(false)}
                >
                  <UserPlusIcon size={14} className="hidden lg:block" />
                  {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.CREATE_ACCOUNT")}
                </button>
                <button
                  type="button"
                  className={cn(
                    "flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-transparent px-4 py-2 text-xs transition-colors",
                    isGuestCheckout
                      ? "border-dodger-blue-700 bg-white text-foreground"
                      : "text-muted-foreground hover:bg-gray-100",
                  )}
                  onClick={() => onGuestCheckoutToggle(true)}
                >
                  <UserIcon size={14} className="hidden lg:block" />
                  {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.ORDER_AS_GUEST")}
                </button>
              </div>

              {isGuestCheckout && (
                <div className="fade-in slide-in-from-top-2 mt-3 flex animate-in items-start gap-3 rounded-xl bg-amber-50 fill-mode-both p-4 text-amber-800 text-sm leading-relaxed duration-300">
                  <Info size={20} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
                  <p>{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.GUEST_CHECKOUT_INFO")}</p>
                </div>
              )}
            </div>
          )}

          {/* "Nie znaleźliśmy konta…" notice for the create-account path. Rendered
              under the tabs because picking "Zamów jako gość" replaces it with the
              amber GUEST_CHECKOUT_INFO inside the tabs block. When tabs aren't shown
              (simplifiedRegistration unavailable) this falls back to the top of the
              registration section. */}
          {phoneCheckoutStep === "NewUser" && !isGuestCheckout && (
            <div className="fade-in slide-in-from-top-2 flex animate-in items-start gap-3 rounded-xl bg-blue-50 fill-mode-both p-4 text-blue-800 text-sm leading-relaxed duration-300">
              <Info size={20} weight="fill" className="mt-0.5 shrink-0 text-blue-600" />
              <p>{t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.NEW_USER_NOTICE")}</p>
            </div>
          )}

          {readonlyCustomerFields ? (
            (() => {
              const readonlyEmail = values.email || prefilledUserData?.email;
              return (
                <div className="flex flex-col gap-4 rounded-md p-5 pt-0">
                  <div className="flex items-center border-gray-100 border-b pb-3">
                    <span className="w-1/2 text-muted-foreground">
                      {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FIRST_NAME")}
                    </span>
                    <span className="font-medium text-foreground">
                      {values.firstName || prefilledUserData?.firstName}
                    </span>
                  </div>
                  <div
                    className={cn(
                      "flex items-center",
                      // Drop the trailing border when there's no email row beneath.
                      readonlyEmail && "border-gray-100 border-b pb-3",
                    )}
                  >
                    <span className="w-1/2 text-muted-foreground">
                      {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.LAST_NAME")}
                    </span>
                    <span className="font-medium text-foreground">
                      {values.lastName || prefilledUserData?.lastName}
                    </span>
                  </div>
                  {readonlyEmail && (
                    <div className="flex items-center">
                      <span className="w-1/2 text-muted-foreground">
                        {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.EMAIL")}
                      </span>
                      <span className="font-medium text-foreground">{readonlyEmail}</span>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <>
              <div className="flex flex-col gap-5 lg:flex-row">
                <div className="flex-1">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="firstName">
                      {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FIRST_NAME")} {readonlyCustomerFields ? "" : "*"}
                    </Label>
                    <Input
                      ref={register}
                      name="firstName"
                      required={!readonlyCustomerFields}
                      disabled={readonlyCustomerFields}
                      autoComplete="new-name"
                      aria-invalid={!!errors.firstName?.message}
                    />
                  </div>
                  {errors.firstName?.message ? (
                    <p className="mt-2 font-medium text-destructive text-sm">{errors.firstName.message}</p>
                  ) : (
                    <p className="mt-2 text-muted-foreground text-xs">
                      {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FIRST_NAME_HINT")}
                    </p>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="lastName">
                      {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.LAST_NAME")} {readonlyCustomerFields ? "" : "*"}
                    </Label>
                    <Input
                      ref={register}
                      name="lastName"
                      required={!readonlyCustomerFields}
                      disabled={readonlyCustomerFields}
                      autoComplete="new-name"
                      aria-invalid={!!errors.lastName?.message}
                    />
                  </div>
                  {errors.lastName?.message && (
                    <p className="mt-2 font-medium text-destructive text-sm">{errors.lastName.message}</p>
                  )}
                </div>
              </div>
              {!isGuestCheckout && (
                <div className="fade-in slide-in-from-top-2 flex animate-in flex-col gap-5 fill-mode-both duration-300 lg:flex-row">
                  <div className="flex-1">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="email">
                        {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.EMAIL")} {readonlyCustomerFields ? "" : "*"}
                      </Label>
                      <Input
                        ref={register}
                        name="email"
                        required={!readonlyCustomerFields}
                        disabled={readonlyCustomerFields}
                        aria-invalid={!!errors.email?.message}
                      />
                    </div>
                    {errors.email?.message && (
                      <p className="mt-2 font-medium text-destructive text-sm">{errors.email.message}</p>
                    )}
                  </div>
                  {!readonlyCustomerFields && (
                    <div className="flex-1">
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="emailConfirmation">
                          {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.EMAIL_CONFIRMATION")} *
                        </Label>
                        <Input
                          ref={register}
                          name="emailConfirmation"
                          required
                          aria-invalid={!!errors.emailConfirmation?.message}
                        />
                      </div>
                      {errors.emailConfirmation?.message && (
                        <p className="mt-2 font-medium text-destructive text-sm">{errors.emailConfirmation.message}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {showPasswordFields && !isGuestCheckout && (
            <div className="fade-in slide-in-from-top-2 flex animate-in flex-col gap-5 fill-mode-both duration-300 lg:flex-row">
              <div className="flex-1">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="password">Hasło *</Label>
                  <Input
                    ref={register}
                    name="password"
                    type="password"
                    required
                    aria-invalid={!!errors.password?.message}
                  />
                </div>
                {errors.password?.message && (
                  <p className="mt-2 font-medium text-destructive text-sm">{errors.password.message}</p>
                )}
              </div>
              <div className="flex-1">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="passwordConfirmation">Potwierdź hasło *</Label>
                  <Input
                    ref={register}
                    name="passwordConfirmation"
                    type="password"
                    required
                    aria-invalid={!!errors.passwordConfirmation?.message}
                  />
                </div>
                {errors.passwordConfirmation?.message && (
                  <p className="mt-2 font-medium text-destructive text-sm">{errors.passwordConfirmation.message}</p>
                )}
              </div>
            </div>
          )}

          {variant === "standard" && (
            <div className="flex flex-col gap-5">
              <PeselSection coreRequired={coreRequired} noPesel={noPesel} orderType={orderType} />
            </div>
          )}

          {!phoneCheckoutStep && (
            <div className="flex gap-3">
              <Form.Control className="w-36">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="dialCode">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.COUNTRY")} *</Label>
                  <Select defaultValue="+48" ref={register} name="dialCode" aria-invalid={!!errors.dialCode?.message}>
                    {countryCodes.map((country) => (
                      <Select.Option value={country.dial_code.replace(/ /g, "")} key={country.code}>
                        ({country.dial_code})
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </Form.Control>
              <div className="w-full">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="phone">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PHONE")} *</Label>
                  <Controller
                    control={control}
                    name="phone"
                    render={({ onChange, onBlur, value, ref }) => (
                      <Input
                        name="phone"
                        ref={ref}
                        value={value}
                        onChange={onChange}
                        onBlur={() => {
                          onChange(value?.trim() || "");
                          onBlur();
                        }}
                        required
                        inputMode="numeric"
                        aria-invalid={!!errors.phone?.message}
                      />
                    )}
                  />
                </div>
                {errors.phone?.message && (
                  <p className="mt-2 font-medium text-destructive text-sm">{errors.phone.message}</p>
                )}
              </div>
            </div>
          )}
          <ConsentsSection
            showMarketingConsent={showMarketingConsent}
            isSelectAllChecked={isSelectAllChecked}
            consentFieldsForSelectAll={consentFieldsForSelectAll}
            isGuestCheckout={isGuestCheckout}
            privacyPolicyHref={privacyPolicyHref}
            termsHref={termsHref}
          />
        </div>
      )}
    </>
  );
};
